/*
 * Consent — Plane VII (§42–§47).
 *
 * The consent service is NOT up in this deployment. Per the provability law we
 * NEVER fabricate consent records: every data surface binds to consent.* (which
 * rejects with PlaneOfflineError) and renders the honest PlaneState offline panel.
 *
 * BUT the consent GATE is a first-class component that can visibly DENY — no
 * consent-theatre (consentement de façade). A gate decision is one of:
 *   - GRANTED  (teal)   — a live, valid, unexpired, unrevoked consent record proves it
 *   - DENIED   (red)    — explicitly denied / revoked / expired — LOUD
 *   - INDETERMINATE → fails CLOSED to DENIED (red) when the consent service is
 *     offline and cannot substantiate consent. Fail-closed is the only safe
 *     posture: absence of proof of consent is treated as denial, loudly.
 *
 * The gate is built so that a DENY is always reachable and always loud,
 * independent of whether the backing service is reachable.
 */

import { useCallback, useEffect, useState } from "react";
import {
  consent,
  isPlaneOffline,
  isMeshApiError,
  PlaneOfflineError,
  type ConsentRecordsResponse,
} from "../api/client";
import { usePlaneHealth, type PlaneHealthState } from "../api/metrics";
import { PlaneState } from "../components/PlaneState";
import { SpecRef } from "../components/SpecRef";
import { VerifyBadge } from "../components/VerifyBadge";
import { Hash } from "../components/Hash";
import { ProvabilityDrawer } from "../components/ProvabilityDrawer";
import type { Claim, Role, VerifyState } from "../api/types";

const SPEC_REF = "Part VII §42–§47";

/* ------------------------------------------------------------------ *
 * Gate decision model — the heart of the view.
 * ------------------------------------------------------------------ */

type GateVerdict = "granted" | "denied" | "indeterminate" | "gated";

interface GateDecision {
  verdict: GateVerdict;
  /** Why the gate decided this way — shown verbatim, never softened. */
  reason: string;
  /** The data subject the gate was asked about. */
  subject: string;
  /** The purpose the relying party requested access for. */
  purpose: string;
  /** When the decision was reached. */
  decidedAt: number;
  /** Offline error when the gate could not reach the consent service. */
  offline?: PlaneOfflineError;
}

/** Map a gate verdict to its loud, semantic VerifyState. */
function gateBadgeState(v: GateVerdict): VerifyState {
  if (v === "granted") return "verified";
  if (v === "denied") return "failed"; // DENIED is loud/red.
  if (v === "gated") return "attested"; // live, correctly access-gated — neutral gold, not red.
  return "offline"; // indeterminate (fail-closed) — red, substantiated as "cannot prove"
}

function gateWord(v: GateVerdict): string {
  if (v === "granted") return "CONSENT GRANTED";
  if (v === "denied") return "CONSENT DENIED";
  if (v === "gated") return "ACCESS GATED — OPERATOR CREDENTIAL REQUIRED";
  return "GATE FAILS CLOSED — DENIED";
}

/** Load state for the live GET /v1/consent/records read. */
type RecordsState =
  | { kind: "loading" }
  | { kind: "offline"; offline: PlaneOfflineError }
  | { kind: "gated" } // live, correctly refused (403) — anonymous by design
  | { kind: "error"; message: string }
  | { kind: "ready"; data: ConsentRecordsResponse };

/* ------------------------------------------------------------------ *
 * View
 * ------------------------------------------------------------------ */

export default function Consent({ role }: { role: Role }) {
  const isOperator = role === "operator";

  // The drawer the whole view shares — any badge/hash can open a proof receipt.
  const [claim, setClaim] = useState<Claim | null>(null);

  // Live health probe of the deployed consent-plane stub. When up, offline
  // surfaces read "deployed · health 200 · API pending" instead of "not running".
  // NOTE: this does NOT relax the gate — a gate that cannot prove consent still
  // fails closed to DENIED. Health-up only means "service deployed, API pending".
  const { state: health } = usePlaneHealth(consent.health);
  const serviceHealthy = health.kind === "up";

  // Records surface — bound to the LIVE consent.listRecords(). Anonymous
  // console sessions get a 403 (operator-gated by design, §39.3.1/§47.2) —
  // that is a distinct honest state from both "offline" and "empty".
  const [recordsState, setRecordsState] = useState<RecordsState>({ kind: "loading" });

  const probeRecords = useCallback(() => {
    setRecordsState({ kind: "loading" });
    consent
      .listRecords()
      .then((data) => setRecordsState({ kind: "ready", data }))
      .catch((e) => {
        if (isPlaneOffline(e)) setRecordsState({ kind: "offline", offline: e });
        else if (isMeshApiError(e) && e.status === 403) setRecordsState({ kind: "gated" });
        else
          setRecordsState({
            kind: "error",
            message: e instanceof Error ? e.message : "Unknown error",
          });
      });
  }, []);

  useEffect(() => {
    probeRecords();
  }, [probeRecords]);

  return (
    <section>
      <header className="view-header">
        <div className="view-header__eyebrow">
          <span className="accent-indigo">Consent</span>
          <span><SpecRef text="Plane VII · §42–§47" /></span>
        </div>
        <h1 className="view-header__title">The consent gate</h1>
        <p className="view-header__sub">
          Consent records, the access gate that can visibly deny, SD-JWT-VC
          verification, USSD/SMS capture channels, and right-to-be-forgotten.
          A gate that cannot prove consent <strong>fails closed</strong> — and
          says so loudly. Role: <strong>{role}</strong>.
        </p>
      </header>

      {/* The gate is FIRST — it is the product. It can DENY regardless of
          whether the records service is reachable. */}
      <ConsentGate onInspect={setClaim} />

      <div style={{ height: "var(--space-6)" }} />

      <div style={gridStyle}>
        {/* Consent records — live, operator-gated by design. */}
        <Block
          title="Consent records"
          accentClass="accent-indigo"
          note="Every record is hash-chained and carries its own proof. The console will render records only from live data — never a placeholder."
        >
          {recordsState.kind === "loading" && <PlaneState kind="loading" />}
          {recordsState.kind === "offline" && (
            <PlaneState
              kind="offline"
              offline={recordsState.offline}
              serviceHealthy={serviceHealthy}
              serviceProbing={health.kind === "probing"}
              serviceLatencyMs={health.kind === "up" ? health.latencyMs : undefined}
              specRef={SPEC_REF}
              endpoints={["GET /v1/consent/records", "GET /v1/consent/records/{id}"]}
              onRetry={probeRecords}
            />
          )}
          {recordsState.kind === "gated" && (
            <PlaneState
              kind="gated"
              specRef={SPEC_REF}
              endpoints={["GET /v1/consent/records"]}
              onRetry={probeRecords}
            />
          )}
          {recordsState.kind === "error" && (
            <PlaneState kind="error" message={recordsState.message} onRetry={probeRecords} />
          )}
          {recordsState.kind === "ready" && recordsState.data.records.length === 0 && (
            <PlaneState
              kind="empty"
              title="No consent records observed"
              message="The service is reachable but returned no records."
            />
          )}
          {recordsState.kind === "ready" && recordsState.data.records.length > 0 && (
            <RecordsList data={recordsState.data} onInspect={setClaim} />
          )}
        </Block>

        {/* SD-JWT-VC verification. */}
        <Block
          title="SD-JWT-VC verification"
          accentClass="accent-gold"
          note={
            serviceHealthy
              ? "Paste a selectively-disclosed credential to verify its issuer signature and disclosed claims. The consent service is deployed and healthy; the verifier read-API is a pending increment — until it binds, no credential is claimed valid."
              : "Paste a selectively-disclosed credential to verify its issuer signature and disclosed claims. Verification runs against the consent verifier — a failed or unreachable verifier is shown loudly, never as a pass."
          }
        >
          <SdJwtVcPanel health={health} onInspect={setClaim} />
        </Block>

        {/* USSD / SMS channel status. */}
        <Block
          title="Capture channels — USSD / SMS"
          accentClass="accent-ember"
          note={
            serviceHealthy
              ? "Low-bandwidth consent capture for feature-phone subjects. The consent service is deployed and healthy; channel state awaits the channels read-API (a pending increment)."
              : "Low-bandwidth consent capture for feature-phone subjects. Channel health binds to the consent service; offline until it is up."
          }
        >
          <ChannelStatus health={health} />
        </Block>

        {/* RTBF. */}
        <Block
          title="Right-to-be-forgotten (RTBF)"
          accentClass="accent-red"
          note="Erasure is irreversible and operator-gated. The request is signed and audit-logged; it is never a quiet success."
        >
          <RtbfPanel
            isOperator={isOperator}
            health={health}
            onInspect={setClaim}
          />
        </Block>
      </div>

      <ProvabilityDrawer claim={claim} onClose={() => setClaim(null)} />
    </section>
  );
}

/* ================================================================== *
 * Consent gate
 * ================================================================== */

function ConsentGate({ onInspect }: { onInspect: (c: Claim) => void }) {
  const [subject, setSubject] = useState("MSISDN:+254700000001");
  const [purpose, setPurpose] = useState("tax-record-share");
  const [decision, setDecision] = useState<GateDecision | null>(null);
  const [evaluating, setEvaluating] = useState(false);

  const evaluate = useCallback(() => {
    setEvaluating(true);
    setDecision(null);
    // The gate asks the consent service for a live, valid record proving the
    // subject consented to this purpose. The service is offline → the gate
    // CANNOT substantiate consent → it fails CLOSED to DENIED. This is the
    // honest, safe outcome, surfaced as a LOUD red state.
    consent
      .getRecord(`${subject}::${purpose}`)
      .then(() => {
        // Would only happen once a live service returns a valid record.
        setDecision({
          verdict: "granted",
          reason:
            "A live, unexpired, unrevoked consent record proves the subject consented to this purpose.",
          subject,
          purpose,
          decidedAt: Date.now(),
        });
      })
      .catch((e) => {
        if (isPlaneOffline(e)) {
          setDecision({
            verdict: "indeterminate",
            reason:
              "The consent service is offline, so no consent record could be retrieved to prove the subject's consent. Absence of proof is treated as denial — the gate fails closed.",
            subject,
            purpose,
            decidedAt: Date.now(),
            offline: e,
          });
        } else if (isMeshApiError(e) && e.status === 403) {
          // The service is LIVE and correctly refused — this console session
          // has no operator/DPA credential (§39.3.1/§47.2). That is distinct
          // from "no consent record exists": say so honestly, never implying
          // the subject was denied consent on the merits.
          setDecision({
            verdict: "gated",
            reason:
              "The consent service is live and answered, but reading a record requires an authenticated operator/DPA credential this console session does not carry. This gate has NOT determined whether the subject consented — it could not check.",
            subject,
            purpose,
            decidedAt: Date.now(),
          });
        } else {
          setDecision({
            verdict: "denied",
            reason:
              "No valid consent record was found for this subject and purpose. Access is denied.",
            subject,
            purpose,
            decidedAt: Date.now(),
          });
        }
      })
      .finally(() => setEvaluating(false));
  }, [subject, purpose]);

  const denied = decision?.verdict === "denied";
  const indeterminate = decision?.verdict === "indeterminate";
  const granted = decision?.verdict === "granted";
  const gated = decision?.verdict === "gated";
  const loud = denied || indeterminate;

  const gateClaim: Claim | null = decision
    ? {
        label: `Consent gate decision — ${gateWord(decision.verdict)}`,
        verdict: gateBadgeState(decision.verdict),
        evidence: granted
          ? {
              specRef: SPEC_REF,
              detail: [
                { label: "subject", value: decision.subject },
                { label: "purpose", value: decision.purpose },
                { label: "verdict", value: "GRANTED" },
                {
                  label: "decided",
                  value: new Date(decision.decidedAt).toISOString(),
                },
              ],
              source: "GET /v1/consent/records/{id}",
            }
          : decision.verdict === "gated"
            ? {
                specRef: SPEC_REF,
                detail: [
                  { label: "subject", value: decision.subject },
                  { label: "purpose", value: decision.purpose },
                  { label: "verdict", value: "NOT DETERMINED — access gated (403)" },
                  {
                    label: "decided",
                    value: new Date(decision.decidedAt).toISOString(),
                  },
                ],
                source: "GET /v1/consent/records/{id} (403 — operator credential required)",
              }
          : decision.offline
            ? {
                specRef: SPEC_REF,
                detail: [
                  { label: "subject", value: decision.subject },
                  { label: "purpose", value: decision.purpose },
                  { label: "verdict", value: "DENIED (fail-closed)" },
                  { label: "cause", value: "consent service offline" },
                  { label: "awaiting service", value: decision.offline.service },
                  {
                    label: "decided",
                    value: new Date(decision.decidedAt).toISOString(),
                  },
                ],
                source: "GET /v1/consent/records/{id} (unreachable)",
              }
            : {
                specRef: SPEC_REF,
                detail: [
                  { label: "subject", value: decision.subject },
                  { label: "purpose", value: decision.purpose },
                  { label: "verdict", value: "DENIED" },
                  {
                    label: "decided",
                    value: new Date(decision.decidedAt).toISOString(),
                  },
                ],
                source: "GET /v1/consent/records/{id}",
              },
      }
    : null;

  return (
    <div
      className="panel"
      style={{
        borderColor: granted
          ? "rgba(var(--teal-rgb),0.35)"
          : loud
            ? "rgba(var(--red-rgb),0.45)"
            : "var(--hair)",
        background: loud
          ? "var(--red-dim)"
          : granted
            ? "var(--teal-dim)"
            : "var(--bg-2)",
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-4)",
      }}
      role={loud ? "alert" : "group"}
      aria-label="Consent gate"
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--space-3)",
          flexWrap: "wrap",
        }}
      >
        <span
          className="mono"
          style={{
            fontSize: "11px",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "var(--fg-faint)",
          }}
        >
          The consent gate
        </span>
        <span style={{ flex: "1 1 auto" }} />
        <span className="mono" style={{ fontSize: "12px", color: "var(--indigo)" }}>
          {SPEC_REF}
        </span>
      </div>

      <p style={{ color: "var(--fg-muted)", margin: 0, maxWidth: "70ch" }}>
        Before any cross-domain data flows, the gate must produce a positive
        consent decision. A subject who did not consent — or whose consent was
        revoked, expired, or cannot be proven — is{" "}
        <strong>denied at the gate</strong>. There is no implicit pass.
      </p>

      {/* Inputs */}
      <div style={gateFormStyle}>
        <Field
          id="gate-subject"
          label="Mesh-Source subject"
          value={subject}
          onChange={setSubject}
          placeholder="MSISDN:+2547…"
        />
        <Field
          id="gate-purpose"
          label="Requested purpose"
          value={purpose}
          onChange={setPurpose}
          placeholder="tax-record-share"
        />
        <button
          type="button"
          onClick={evaluate}
          disabled={evaluating || !subject.trim() || !purpose.trim()}
          style={primaryBtnStyle(evaluating)}
        >
          {evaluating ? "Evaluating gate…" : "Evaluate gate"}
        </button>
      </div>

      {/* Decision banner — the loud part */}
      {decision && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--space-3)",
            borderTop: "1px solid var(--hair)",
            paddingTop: "var(--space-4)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--space-3)",
              flexWrap: "wrap",
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: "14px",
                height: "14px",
                borderRadius: "50%",
                background: granted ? "var(--teal)" : gated ? "var(--gold)" : "var(--red)",
                boxShadow: granted
                  ? "0 0 0 5px var(--teal-dim)"
                  : gated
                    ? "0 0 0 5px var(--gold-dim)"
                    : "0 0 0 5px var(--red-dim)",
                flex: "0 0 auto",
              }}
            />
            <strong
              style={{
                fontSize: "20px",
                fontFamily: "var(--font-display)",
                color: granted ? "var(--teal)" : gated ? "var(--gold)" : "var(--red)",
                letterSpacing: "0.01em",
              }}
            >
              {gateWord(decision.verdict)}
            </strong>
            {gateClaim && (
              <VerifyBadge
                state={gateBadgeState(decision.verdict)}
                label={
                  granted
                    ? "Verified"
                    : gated
                      ? "Not determined — access gated"
                      : indeterminate
                        ? "Denied (fail-closed)"
                        : "Denied"
                }
                onInspect={() => onInspect(gateClaim)}
              />
            )}
          </div>

          <p
            style={{
              margin: 0,
              color: granted ? "var(--fg)" : gated ? "var(--fg)" : "var(--red)",
              fontWeight: loud ? 600 : 400,
              maxWidth: "72ch",
            }}
          >
            {decision.reason}
          </p>

          <div
            className="mono"
            style={{
              fontSize: "12px",
              color: "var(--fg-faint)",
              display: "flex",
              flexWrap: "wrap",
              gap: "var(--space-2) var(--space-5)",
            }}
          >
            <span>
              subject <span style={{ color: "var(--fg)" }}>{decision.subject}</span>
            </span>
            <span>
              purpose <span style={{ color: "var(--fg)" }}>{decision.purpose}</span>
            </span>
            <span>
              decided{" "}
              <span style={{ color: "var(--fg)" }}>
                {new Date(decision.decidedAt).toISOString()}
              </span>
            </span>
          </div>

          {decision.offline && (
            <PlaneState
              kind="offline"
              offline={decision.offline}
              specRef={SPEC_REF}
              title="Why the gate failed closed"
              message="The gate asked the consent service for a record proving consent and the service is not running. The gate denies rather than guesses — fail-closed is the only safe outcome."
              endpoints={["GET /v1/consent/records/{id}"]}
            />
          )}
        </div>
      )}

      {!decision && !evaluating && (
        <p
          className="mono"
          style={{ fontSize: "12px", color: "var(--fg-faint)", margin: 0 }}
        >
          No decision yet — the gate denies by default until a positive,
          provable consent is produced.
        </p>
      )}
    </div>
  );
}

/* ================================================================== *
 * SD-JWT-VC verification panel
 * ================================================================== */

function SdJwtVcPanel({
  health,
  onInspect,
}: {
  health: PlaneHealthState;
  onInspect: (c: Claim) => void;
}) {
  const healthy = health.kind === "up";
  const probing = health.kind === "probing";
  const [token, setToken] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [result, setResult] = useState<
    | { kind: "offline"; offline: PlaneOfflineError }
    | { kind: "failed"; reason: string }
    | null
  >(null);

  const verify = useCallback(() => {
    const trimmed = token.trim();
    if (!trimmed) {
      setResult({
        kind: "failed",
        reason: "No credential supplied. Nothing to verify.",
      });
      return;
    }
    setVerifying(true);
    setResult(null);
    // No SD-JWT-VC verification route exists on the consent service yet — this
    // is NOT the same gap as the operator-gated /v1/consent/records (which IS
    // wired). We never call a real endpoint for an unrelated purpose just to
    // manufacture an offline state, so this stays a direct, honest offline
    // result with no network call.
    setResult({
      kind: "offline",
      offline: new PlaneOfflineError("Consent", "consent-sdjwt-verifier"),
    });
    setVerifying(false);
  }, [token]);

  const offlineClaim: Claim | null =
    result?.kind === "offline"
      ? {
          label: "SD-JWT-VC verification — could not run",
          verdict: "offline",
          evidence: {
            specRef: SPEC_REF,
            algorithm: "ES256 / EdDSA (issuer-dependent)",
            detail: [
              { label: "verifier", value: result.offline.service },
              { label: "status", value: "offline — verification not run" },
            ],
            source: "POST /v1/consent/sd-jwt-vc/verify (unreachable)",
          },
        }
      : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
      <label htmlFor="sdjwt-input" style={labelStyle}>
        Selectively-disclosed credential
      </label>
      <textarea
        id="sdjwt-input"
        value={token}
        onChange={(e) => setToken(e.target.value)}
        placeholder="eyJhbGciOi…~WyJzYWx0Iiwi…~"
        spellCheck={false}
        rows={3}
        style={textareaStyle}
      />
      <div style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={verify}
          disabled={verifying}
          style={primaryBtnStyle(verifying)}
        >
          {verifying ? "Verifying…" : "Verify credential"}
        </button>
        {result?.kind === "offline" && offlineClaim && (
          <VerifyBadge
            state={healthy ? "pending" : probing ? "unknown" : "offline"}
            label={healthy ? "API pending" : probing ? "Checking…" : "Could not verify"}
            onInspect={() => onInspect(offlineClaim)}
          />
        )}
        {result?.kind === "failed" && (
          <VerifyBadge state="failed" label="Verification failed" />
        )}
      </div>

      {result?.kind === "offline" && (
        <PlaneState
          kind="offline"
          offline={result.offline}
          serviceHealthy={healthy}
          serviceProbing={health.kind === "probing"}
          serviceLatencyMs={health.kind === "up" ? health.latencyMs : undefined}
          specRef={SPEC_REF}
          title={
            healthy
              ? "Consent service deployed — verifier API pending"
              : "Verifier offline — credential NOT verified"
          }
          message={
            healthy
              ? "The consent service is deployed and reporting healthy. The issuer-signature and disclosure check binds to a verifier read-API that is a pending increment — until it is wired, the console will not claim a credential is valid."
              : "The issuer-signature and disclosure check could not run because the consent verifier is not up. The console will not claim a credential is valid without running the check."
          }
          endpoints={["POST /v1/consent/sd-jwt-vc/verify"]}
        />
      )}
      {result?.kind === "failed" && (
        <PlaneState
          kind="error"
          title="Verification did not pass"
          message={result.reason}
        />
      )}
    </div>
  );
}

/* ================================================================== *
 * USSD / SMS channel status
 * ================================================================== */

function ChannelStatus({ health }: { health: PlaneHealthState }) {
  const healthy = health.kind === "up";
  const probing = health.kind === "probing";
  const [offline, setOffline] = useState<PlaneOfflineError | null>(null);
  const [loading, setLoading] = useState(true);

  const probe = useCallback(() => {
    setLoading(true);
    consent
      .listRecords()
      .catch((e) => {
        if (isPlaneOffline(e)) setOffline(e);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    probe();
  }, [probe]);

  // When the consent service's health stub is up, the channels READ-API is
  // simply a pending increment — calm indigo, never the alarm-red of offline.
  // While the probe is in flight, stay NEUTRAL — never flash a false "offline".
  const chipColor = healthy || probing ? "var(--indigo)" : "var(--red)";
  const chipShadow =
    healthy || probing ? "0 0 0 3px var(--indigo-dim)" : "0 0 0 3px var(--red-dim)";
  const chipLabel = healthy
    ? "awaiting channels API"
    : probing
      ? "checking channel health…"
      : "channel offline";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
      <ul style={channelListStyle}>
        {(["USSD", "SMS"] as const).map((ch) => (
          <li key={ch} style={channelRowStyle}>
            <span
              aria-hidden="true"
              style={{
                width: "9px",
                height: "9px",
                borderRadius: "50%",
                background: chipColor,
                boxShadow: chipShadow,
                flex: "0 0 auto",
              }}
            />
            <span className="mono" style={{ fontSize: "13px", color: "var(--fg)" }}>
              {ch}
            </span>
            <span style={{ flex: "1 1 auto" }} />
            <span
              className="mono"
              style={{ fontSize: "11.5px", color: chipColor, fontWeight: 600 }}
            >
              {chipLabel}
            </span>
          </li>
        ))}
      </ul>
      {loading ? (
        <PlaneState kind="loading" />
      ) : offline ? (
        <PlaneState
          kind="offline"
          offline={offline}
          serviceHealthy={healthy}
          serviceProbing={health.kind === "probing"}
          serviceLatencyMs={health.kind === "up" ? health.latencyMs : undefined}
          specRef={SPEC_REF}
          title={
            healthy ? "Consent service deployed — channels API pending" : undefined
          }
          message={
            healthy
              ? "The consent service is deployed and reporting healthy. USSD/SMS capture-gateway state binds to a channels read-API that is a pending increment — channel state is shown as pending rather than assumed healthy."
              : "USSD/SMS capture gateways report their health through the consent service, which is not running. Channel state is shown as offline rather than assumed healthy."
          }
          endpoints={["GET /v1/consent/channels"]}
          onRetry={probe}
        />
      ) : null}
    </div>
  );
}

/* ================================================================== *
 * RTBF panel
 * ================================================================== */

function RtbfPanel({
  isOperator,
  health,
  onInspect,
}: {
  isOperator: boolean;
  health: PlaneHealthState;
  onInspect: (c: Claim) => void;
}) {
  const healthy = health.kind === "up";
  const probing = health.kind === "probing";
  const [subject, setSubject] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<
    | { kind: "offline"; offline: PlaneOfflineError }
    | { kind: "error"; reason: string }
    | null
  >(null);

  const submit = useCallback(() => {
    const s = subject.trim();
    if (!s) return;
    setSubmitting(true);
    setResult(null);
    consent
      .requestErasure(s)
      .catch((e) => {
        if (isPlaneOffline(e)) setResult({ kind: "offline", offline: e });
        else
          setResult({
            kind: "error",
            reason: "The erasure request was not accepted by the service.",
          });
      })
      .finally(() => {
        setSubmitting(false);
        setConfirm(false);
      });
  }, [subject]);

  const offlineClaim: Claim | null =
    result?.kind === "offline"
      ? {
          label: "RTBF erasure — request not lodged",
          verdict: "offline",
          evidence: {
            specRef: SPEC_REF,
            detail: [
              { label: "subject", value: subject.trim() },
              { label: "service", value: result.offline.service },
              { label: "status", value: "offline — request not lodged" },
            ],
            source: "POST /v1/consent/rtbf (unreachable)",
          },
        }
      : null;

  if (!isOperator) {
    return (
      <div
        className="panel panel--inset"
        style={{ borderColor: "var(--hair)" }}
        role="note"
      >
        <p style={{ margin: 0, color: "var(--fg-muted)" }}>
          Right-to-be-forgotten requests are{" "}
          <strong>operator-only</strong>. The current role cannot lodge an
          erasure. Switch to the operator role to act.
        </p>
        <div style={{ marginTop: "var(--space-3)" }}>
          <Hash
            value="role:auditor"
            label="active role"
            head={0}
            tail={0}
            accent="var(--fg-muted)"
          />
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
      <Field
        id="rtbf-subject"
        label="Data subject to forget"
        value={subject}
        onChange={setSubject}
        placeholder="MSISDN:+2547… or subject DID"
      />

      <label
        style={{
          display: "flex",
          gap: "var(--space-3)",
          alignItems: "flex-start",
          color: "var(--fg-muted)",
          fontSize: "13px",
          minHeight: "var(--tap)",
          cursor: "pointer",
        }}
      >
        <input
          type="checkbox"
          checked={confirm}
          onChange={(e) => setConfirm(e.target.checked)}
          style={{ width: "20px", height: "20px", marginTop: "2px", accentColor: "var(--red)" }}
        />
        <span>
          I understand erasure is <strong>irreversible</strong> and will be
          signed and audit-logged under my operator identity.
        </span>
      </label>

      <div style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={submit}
          disabled={submitting || !confirm || !subject.trim()}
          style={dangerBtnStyle(submitting || !confirm || !subject.trim())}
        >
          {submitting ? "Lodging erasure…" : "Lodge RTBF erasure"}
        </button>
        {result?.kind === "offline" && offlineClaim && (
          <VerifyBadge
            state={healthy ? "pending" : probing ? "unknown" : "offline"}
            label={healthy ? "API pending" : probing ? "Checking…" : "Not lodged"}
            onInspect={() => onInspect(offlineClaim)}
          />
        )}
        {result?.kind === "error" && (
          <VerifyBadge state="failed" label="Rejected" />
        )}
      </div>

      {result?.kind === "offline" && (
        <PlaneState
          kind="offline"
          offline={result.offline}
          serviceHealthy={healthy}
          serviceProbing={health.kind === "probing"}
          serviceLatencyMs={health.kind === "up" ? health.latencyMs : undefined}
          specRef={SPEC_REF}
          title={
            healthy
              ? "Erasure NOT lodged — RTBF API pending"
              : "Erasure NOT lodged — service offline"
          }
          message={
            healthy
              ? "The consent service is deployed and reporting healthy, but the right-to-be-forgotten request binds to an erasure read-API that is a pending increment. Nothing was deleted; the request was not silently dropped — it is shown as not-lodged."
              : "The right-to-be-forgotten request was not accepted because the consent erasure service is not running. Nothing was deleted; the request was not silently dropped — it is shown as not-lodged."
          }
          endpoints={["POST /v1/consent/rtbf"]}
        />
      )}
      {result?.kind === "error" && (
        <PlaneState kind="error" title="Erasure rejected" message={result.reason} />
      )}
    </div>
  );
}

/* ================================================================== *
 * Consent records list — real data, §42.4 pseudonyms-only projection.
 * ================================================================== */

function RecordsList({
  data,
  onInspect,
}: {
  data: ConsentRecordsResponse;
  onInspect: (c: Claim) => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
      <div className="mono" style={{ fontSize: "11.5px", color: "var(--fg-faint)" }}>
        {data.country} · chain head seq {data.chain_head.seq ?? "—"}
      </div>
      <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: "8px" }}>
        {data.records.map((r) => (
          <li
            key={r.consent_id}
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "10px",
              padding: "10px 12px",
              borderRadius: "var(--radius-sm)",
              border: "1px solid var(--hair)",
              background: "var(--bg)",
            }}
          >
            <button
              type="button"
              onClick={() =>
                onInspect({
                  label: `Consent ${r.consent_id}`,
                  verdict: r.state === "active" ? "verified" : "unknown",
                  evidence: {
                    hashes: [{ label: "self_hash", value: r.self_hash }],
                    specRef: SPEC_REF,
                    source: "GET /v1/consent/records",
                    detail: [
                      { label: "state", value: r.state },
                      { label: "pseudonym", value: r.citizen_pseudonym },
                      { label: "purpose", value: r.purpose },
                      { label: "consumer", value: r.consumer },
                      { label: "holder", value: r.holder },
                      { label: "lawful basis", value: r.lawful_basis },
                    ],
                  },
                })
              }
              className="mono"
              style={{ background: "none", border: "none", padding: 0, color: "var(--fg)", cursor: "pointer", fontSize: "12.5px" }}
            >
              {r.consent_id}
            </button>
            <span style={{ color: "var(--fg-muted)", fontSize: "12.5px" }}>{r.purpose}</span>
            <VerifyBadge state={r.state === "active" ? "verified" : "unknown"} label={r.state} size="sm" />
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ================================================================== *
 * Shared local primitives (inline — no shared-file edits)
 * ================================================================== */

function Block({
  title,
  accentClass,
  note,
  children,
}: {
  title: string;
  accentClass: string;
  note: string;
  children: React.ReactNode;
}) {
  return (
    <div className="panel" style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <div>
        <h2
          style={{
            fontSize: "18px",
            display: "flex",
            alignItems: "center",
            gap: "var(--space-3)",
          }}
        >
          <span
            aria-hidden="true"
            className={accentClass}
            style={{ fontSize: "14px" }}
          >
            ●
          </span>
          {title}
        </h2>
        <p
          style={{
            margin: "var(--space-2) 0 0",
            color: "var(--fg-muted)",
            fontSize: "13px",
            maxWidth: "60ch",
          }}
        >
          {note}
        </p>
      </div>
      {children}
    </div>
  );
}

function Field({
  id,
  label,
  value,
  onChange,
  placeholder,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)", flex: "1 1 200px" }}>
      <label htmlFor={id} style={labelStyle}>
        {label}
      </label>
      <input
        id={id}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        spellCheck={false}
        autoCapitalize="none"
        autoCorrect="off"
        style={inputStyle}
      />
    </div>
  );
}

/* ---------- styles ---------- */

const gridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
  gap: "var(--space-5)",
};

const gateFormStyle: React.CSSProperties = {
  display: "flex",
  gap: "var(--space-4)",
  alignItems: "flex-end",
  flexWrap: "wrap",
};

const channelListStyle: React.CSSProperties = {
  listStyle: "none",
  margin: 0,
  padding: 0,
  display: "flex",
  flexDirection: "column",
  gap: "var(--space-2)",
};

const channelRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "var(--space-3)",
  border: "1px solid var(--hair)",
  borderRadius: "var(--radius-sm)",
  padding: "var(--space-3)",
  minHeight: "var(--tap)",
  background: "var(--bg)",
};

const labelStyle: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: "11px",
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "var(--fg-faint)",
};

const inputStyle: React.CSSProperties = {
  background: "var(--bg)",
  color: "var(--fg)",
  border: "1px solid var(--hair-strong)",
  borderRadius: "var(--radius-sm)",
  padding: "0 var(--space-3)",
  minHeight: "var(--tap)",
  fontFamily: "var(--font-mono)",
  fontSize: "13px",
  width: "100%",
};

const textareaStyle: React.CSSProperties = {
  background: "var(--bg)",
  color: "var(--fg)",
  border: "1px solid var(--hair-strong)",
  borderRadius: "var(--radius-sm)",
  padding: "var(--space-3)",
  fontFamily: "var(--font-mono)",
  fontSize: "12.5px",
  width: "100%",
  resize: "vertical",
  lineHeight: 1.5,
};

function primaryBtnStyle(busy: boolean): React.CSSProperties {
  return {
    minHeight: "var(--tap)",
    padding: "0 var(--space-5)",
    borderRadius: "var(--radius-sm)",
    border: "1px solid var(--indigo)",
    background: "var(--indigo-dim)",
    color: "var(--indigo)",
    fontFamily: "var(--font-mono)",
    fontSize: "13px",
    fontWeight: 600,
    opacity: busy ? 0.6 : 1,
  };
}

function dangerBtnStyle(disabled: boolean): React.CSSProperties {
  return {
    minHeight: "var(--tap)",
    padding: "0 var(--space-5)",
    borderRadius: "var(--radius-sm)",
    border: "1px solid var(--red)",
    background: "var(--red-dim)",
    color: "var(--red)",
    fontFamily: "var(--font-mono)",
    fontSize: "13px",
    fontWeight: 700,
    opacity: disabled ? 0.5 : 1,
    cursor: disabled ? "not-allowed" : "pointer",
  };
}
